import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { areaForPath, visibleAreas } from '../../lib/navigation';
import { cn } from '../../lib/cn';
import { Button } from '../ui/Button';
import { IconButton } from '../ui/IconButton';
import { Menu, X } from '../ui/icons';
import { UserMenu } from './UserMenu';

const CONTAINER = 'mx-auto max-w-7xl px-4 sm:px-6 lg:px-8';

/**
 * Two-tier top navigation driven by src/lib/navigation.ts:
 *   row 1 - logo, area tabs (Home · Tasks · Projects & KPIs · Ricoh Spaces · Admin), account menu
 *   row 2 - the pages of the current area only, with the active one highlighted
 * Below md the tabs collapse into a drawer; row 2 scrolls horizontally on tablets.
 */
export function Navbar() {
  const { isAuthenticated, user, login } = useAuth();
  const { pathname } = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const areas = isAuthenticated ? visibleAreas(user) : [];
  const current = areaForPath(pathname);
  const subItems = current && current.key !== 'home' ? current.items : [];

  // Close the drawer on navigation
  useEffect(() => setDrawerOpen(false), [pathname]);

  return (
    <nav aria-label="Main" className="border-b border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <div className={CONTAINER}>
        <div className="flex h-14 items-center justify-between gap-4">
          <div className="flex min-w-0 items-center gap-4 md:gap-6">
            {isAuthenticated && areas.length > 0 && (
              <IconButton
                className="md:hidden"
                aria-label={drawerOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={drawerOpen}
                aria-controls="mobile-nav"
                icon={drawerOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                onClick={() => setDrawerOpen((o) => !o)}
              />
            )}
            <Link to="/" className="shrink-0 text-xl font-bold text-primary-600 dark:text-primary-400">
              Tasky
            </Link>
            {isAuthenticated && areas.length > 0 && (
              <div className="hidden items-center gap-1 md:flex" role="navigation" aria-label="Areas">
                {areas.map((area) => {
                  const active = current?.key === area.key;
                  return (
                    <Link
                      key={area.key}
                      to={area.home}
                      aria-current={active ? 'page' : undefined}
                      className={cn(
                        'inline-flex h-14 items-center whitespace-nowrap border-b-2 px-3 text-sm font-medium transition-colors',
                        active
                          ? 'border-primary-600 text-primary-700 dark:border-primary-400 dark:text-primary-300'
                          : 'border-transparent text-gray-600 hover:border-gray-300 hover:text-gray-900 dark:text-gray-300 dark:hover:border-gray-600 dark:hover:text-white',
                      )}
                    >
                      {area.label}
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
          <div className="flex shrink-0 items-center">
            {isAuthenticated ? (
              <UserMenu />
            ) : (
              <Button onClick={() => login()} size="sm">
                Sign in
              </Button>
            )}
          </div>
        </div>
      </div>

      {isAuthenticated && current && subItems.length > 0 && (
        <div className="border-t border-gray-100 dark:border-gray-700/60">
          <div className={CONTAINER}>
            <div className="-mx-1 flex items-center gap-1 overflow-x-auto py-1" role="navigation" aria-label={current.label}>
              {subItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    cn(
                      'inline-flex h-8 items-center whitespace-nowrap rounded-md px-3 text-sm transition-colors',
                      isActive
                        ? 'bg-primary-50 font-medium text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-100',
                    )
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </div>
          </div>
        </div>
      )}

      {isAuthenticated && drawerOpen && (
        <div id="mobile-nav" className="border-t border-gray-200 md:hidden dark:border-gray-700">
          <div className={cn(CONTAINER, 'py-3')}>
            {areas.map((area) => (
              <div key={area.key} className="mb-3 last:mb-0">
                <Link
                  to={area.home}
                  className={cn(
                    'block rounded-md px-3 py-2 text-sm font-semibold',
                    current?.key === area.key ? 'text-primary-700 dark:text-primary-300' : 'text-gray-900 dark:text-gray-100',
                  )}
                >
                  {area.label}
                </Link>
                {area.items.length > 1 && (
                  <div className="ml-3 flex flex-col">
                    {area.items.map((item) => (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        end={item.end}
                        className={({ isActive }) =>
                          cn(
                            'rounded-md px-3 py-1.5 text-sm',
                            isActive ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300' : 'text-gray-600 dark:text-gray-400',
                          )
                        }
                      >
                        {item.label}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
}
